/* eslint-disable no-loop-func */
/* eslint-disable max-len */

import permissionUser from '@/inheritance/permission-user';
import { sanitizeTitle } from '../../use/sanitizeDocumentTitle';

export default {
    /**
     * Nombre del componente
     */
    name: 'UploadDocument',

    /**
     * Extendemos la funcionalidad del listado de tablas desde src/inheritance/upload-documents.js
     */
    extends: permissionUser,

    /**
     * PROPS DE ENTRADA
     * acceptedFormats: Array de formatos permitidos en el upload, por ejemplo: ['jpg', 'pdf']
     * getCategories: Boolean para saber si se debe obtener las categorías y subcategorías desde el back
     * section: Desde donde se quiere hacer el upload
     * uuid: Identificador de la sección, por ejemplo, uuid del interviniente, de la cuenta...
     * category: Categoría pasada por parámetro, en bastanteo
     * subcategory: Subategoría pasada por parámetro, en bastanteo
     */
    props: {
        acceptedFormats: {
            type: Array,
            default: () => ['pdf'],
            required: false
        },
        imageFormats: {
            type: Array,
            default: () => ['image/jpeg', 'image/png', 'image/gif'],
            required: false
        },
        getCategories: {
            type: Boolean,
            default: false,
            required: false
        },
        section: {
            type: String,
            required: true
        },
        uuid: {
            type: String,
            required: true
        },
        category: {
            type: Object,
            required: false
        },
        subcategory: {
            type: Object,
            required: false
        },
        single: {
            type: Boolean,
            default: false,
            required: false
        },
        manualSign: {
            type: Boolean,
            default: false,
            required: false
        },
        bankAccount: {
            type: Object,
            required: false
        },
        account: {
            type: Object,
            required: false
        },
        relatedIntervenerUpload: {
            type: String,
            required: false,
            default: null
        }
    },

    /**
     * DATOS DEL COMPONENTE
     * filesArray: Array de archivos en cola para subir
     * filenamesArray: Array de nombre de archivos para subir
     * categoriesArray: Array con las categorías seleccionadas
     * subcategoriesArray: Array con las subcategorías seleccionadas
     * maxSize: Tamaño máximo permitido para subir
     * categories: Categorías obtenidas desde el back
     * uploading: Booleano que indica si hay una subida activa o no
     * @return {Object} Datos del componente
     */
    data() {
        return {
            filesArray: [],
            filenamesArray: [],
            categoriesArray: [],
            subcategoriesArray: [],
            maxSize: 300 * 1024 * 1024, // 300MB
            categories: [],
            uploading: false,
            validated: false,
            bankAccounts: [],
            selectedBankAccount: false,
            intervenersAccount: [],
            relatedIntervener: null
        };
    },

    /**
     * Configuración inicial del componente
     * 1. Si recibimos getCategories como true hacemos la petición de categorías según la sección que queramos
     */
    mounted() {
        if (this.getCategories) {
            const getCategoriesMethods = {
                intervener: this.$intervenersService.getIntervenerCategories,
                interveners: this.$intervenersService.getIntervenerCategories,
                account: this.$accountsService.getAccountCategories,
                operation: this.$accountsService.getOperationCategories
            };
            getCategoriesMethods[this.section](this.uuid).then(res => {
                this.categories = res.data.document_categories;
                this.categoriesArray = [this.categories[0].key_name];
            });
        }
        if (this.account) {
            const allowedRoles = ["AUTORIZADO_CONSULTA", "AUTORIZADO"];
            this.intervenersAccount = this.account.interveners.filter(i => 
                allowedRoles.some(role => 
                    i.account_roles[0].account_intervener_role.includes(role)
                )
            )
            this.$accountsService.getAccountBankAccounts(this.account.uuid, {})
                .then(res => {
                    this.bankAccounts = res.data.bank_accounts;
                });
        }

        this.syncRelatedIntervener();
    },

    methods: {
        syncRelatedIntervener() {
            const subcategory = this.subcategory && this.subcategory.key_name;
            if (subcategory !== 'AUTORIZACION_A_TERCEROS') return;

            if (!this.relatedIntervener && this.relatedIntervenerUpload) {
                this.relatedIntervener = this.relatedIntervenerUpload;
            }
        },
        /**
         * @override
         */
        onFileChange(e) {
            const maxSize = 300 * 1024 * 1024; // 300MB
            const selectedFiles = e.target.files;

            for (let i = 0; i < selectedFiles.length; i++) {
                if (selectedFiles[i].size < maxSize && this.correctType(selectedFiles[i].type)) {
                    this.filesArray.push(selectedFiles[i]);
                    this.filenamesArray.push(sanitizeTitle(selectedFiles[i].name));
                    // Solo permitir subir un archivo???
                    // this.filesArray = [selectedFiles[i]];
                    // this.filenamesArray = [selectedFiles[i].name];
                } else if (selectedFiles[i].size > maxSize) {
                    this.$toast.error(
                        'Tamaño incorrecto',
                        `No se permite archivos más grandes de 300MB. El archivo ${selectedFiles[i].name} no se puede añadir a la cola de uploads.`
                    );
                } else {
                    this.$toast.error(
                        'Formato incorrecto',
                        `Sólo se pueden subir documentos en formato ${this.formatsAcceptedInFileInput}`
                    );
                }
            }
            let j = 0;
            for (let i = 0; i < this.filesArray.length; i++) {
                const reader = new FileReader();
                reader.onload = () => {
                    if (this.imageType(this.filesArray[i].type)) {
                        this.$refs.image[j].src = reader.result;
                        j++;
                    }
                };
                reader.readAsDataURL(this.filesArray[i]);
            }
        },

        /**
         * UPLOAD DE ARCHIVOS
         * Realizamos la subida de los archivos
         */
        doUpload() {
            this.uploading = true;
            const payload = {
                manualSign: this.manualSign,
                section: this.section,
                uuid: this.uuid,
                files: this.filesArray,
                filesName: this.filenamesArray,
                category:
                    this.categoriesArray && this.categoriesArray.length >= 1
                        ? this.categoriesArray
                        : this.category.key_name,
                subcategory:
                    this.subcategoriesArray && this.subcategoriesArray.length >= 1
                        ? this.subcategoriesArray
                        : this.subcategory.key_name,
                validated: this.validated,
                related_intervener: this.relatedIntervener ?? null
            };

            if (this.bankAccount) {
                payload.uuid_bank_account = this.bankAccount.uuid;
            }

            if (this.selectedBankAccount) {
                payload.uuid_bank_account = this.selectedBankAccount.uuid;
            }

            this.$documentsService
                .upload(payload)
                .then(response => {
                    switch (this.section) {
                        case 'intervener':
                        case 'interveners':
                            this.$root.$emit('refresh_intervener');
                            this.$root.$emit('refresh_account');
                            this.$root.$emit('refresh_operation');
                            break;
                        case 'account':
                            this.$root.$emit('refresh_account');
                            this.$root.$emit('refresh_operation');
                            break;
                        case 'operation':
                            this.$root.$emit('refresh_operation');
                            break;
                        default:
                            break;
                    }
                    this.$emit('uploadOk', response);
                    this.$toast.success(`${response.result.http_code}`, `Archivos añadidos con éxito.`);
                })
                .then(() => this.resetActions())
                .catch(err => {
                    this.resetActions();
                    this.$toast.error(`Error ${err.name}`, `${err.message}`);
                });
        },

        /**
         * ELIMINAR ARCHIVO DE LA COLA
         * Eliminamos un archivo de la cola de subida
         * @param {Number} index Posición del archivo en el array
         */
        removeFile(index) {
            this.filesArray.splice(index, 1);
            this.filenamesArray.splice(index, 1);
            this.$refs.filesRef.value = '';
        },

        /**
         * RESET
         * Vuelve al estado original todas las variables
         */
        resetActions() {
            this.uploading = false;
            this.filesArray = [];
            this.filenamesArray = [];
        },

        /**
         * VERIFICACIÓN DE FORMATO
         * Comprobamos si el tipo de archivo seleccionado está permitido o no
         * @param {String} type Tipo que queremos verificar si es válido o no
         * @return {String} Devuelve un string con el formato seleccionado si es correcto, sino nada
         */
        correctType(type) {
            return this.acceptedFormats.find(x => type.includes(x));
        },

        /**
         * VERIFICACIÓN DE FORMATO DE IMAGEN
         * Comprobamos si el tipo de archivo seleccionado es una imagen
         * @param {String} type Tipo que queremos verificar si es imagen
         * @return {String} Devuelve un string con el formato seleccionado si es imagen, sino nada
         */
        imageType(type) {
            return this.imageFormats.find(x => type.includes(x));
        },

        /**
         * SUBCATEGORÍAS DINÁMICAS
         * Cuando el usuario selecciona una categoría se cargan las subcategorías correspondientes
         * @param {Number} index Posición de la categoria seleccionada
         * @return {Array} Listado de subcategorías
         */
        subcategoriesOptions(index) {
            const categorySelected = this.categories.find(x => x.key_name === this.categoriesArray[index]);
            const indexInArrayCategory = this.categories.indexOf(categorySelected);
            if (this.categoriesArray[index]) {
                const scats = this.categories[indexInArrayCategory].subcategories.map(x => {
                    return {
                        value: x.key_name,
                        text: this.$t(`documents.subcategories.${x.key_name}`)
                    };
                });
                if (!this.subcategoriesArray[index]) {
                    this.subcategoriesArray[index] = scats[0].value;
                }
                return scats;
            } else {
                return [];
            }
        }
    },
    computed: {
        /**
         * Devuelve si tenemos, o no, algún archivo en la cola de subida
         * @return {Boolean} Devuelve si tiene algún documento seleccionado o no
         */
        hasDocuments() {
            return this.filesArray && this.filesArray.length >= 1 ? true : false;
        },

        /**
         * Listado de documents aceptados en el input[type="file"]
         * @return {Array} Listado de formatos mapeadas y formateados
         */
        formatsAcceptedInFileInput() {
            return this.acceptedFormats.map(x => {
                return `.${x}`;
            });
        },

        /**
         * Listado de categorías obtenidas del back y mapeadas
         * @return {Array} Listado de categorías mapeadas
         */
        categoriesOptions() {
            return this.categories.map(x => {
                return {
                    value: x.key_name,
                    text: this.$t(`documents.categories.${x.key_name}`)
                };
            });
        }
    }
};
